export const BEAT_VALUES = { 
  w: 4, h: 2, q: 1, '8': 0.5, '16': 0.25,
  wr: 4, hr: 2, qr: 1, '8r': 0.5, '16r': 0.25
}

export const VOICE_NAMES = ['soprano', 'alto', 'tenor', 'bass']
export const VOICE_LABELS = { soprano: '소프라노', alto: '알토', tenor: '테너', bass: '베이스' }
export const VOICE_COLORS = { soprano: '#2563eb', alto: '#7c3aed', tenor: '#059669', bass: '#dc2626' }

// kordAI 4성부 권장 음역 — theoryBase(baek/kostka)별로 다를 수 있음.
// baek: 백병동 교재 실측치(S C4–A5 / A G3–E5 / T C3–A4 / B E2–E4).
// kostka: Tonal Harmony 본문(Ch.5-9)은 baek처럼 SATB 성부별 숫자 음역표를 제시하지 않음
// (Appendix A는 "악기" 음역·이조표일 뿐, 합창 성부 음역표가 아님) — kostka 쪽은 원서 근거가
// 없는 잠정치이며, 일반적 합창 관행(baek보다 한 음 정도 더 좁게 잡는 편)을 참고해 유지.
// ⚠️ 이 범위는 권장 가이드일 뿐 — 벗어난다고 오류나 감점으로 취급하지 않음(건반·악보에 참고 표시만 함).
export const VOICE_RANGES_BY_THEORY = {
  baek: {
    soprano: { minMidi: 60, maxMidi: 81 },  // C4–A5
    alto:    { minMidi: 55, maxMidi: 76 },  // G3–E5
    tenor:   { minMidi: 48, maxMidi: 69 },  // C3–A4
    bass:    { minMidi: 40, maxMidi: 64 },  // E2–E4
  },
  kostka: {
    soprano: { minMidi: 60, maxMidi: 79 },  // C4–G5 (원서 미제시, 일반 합창 관행 기반 잠정치)
    alto:    { minMidi: 55, maxMidi: 72 },  // G3–C5 (원서 미제시, 일반 합창 관행 기반 잠정치)
    tenor:   { minMidi: 48, maxMidi: 67 },  // C3–G4 (원서 미제시, 일반 합창 관행 기반 잠정치)
    bass:    { minMidi: 40, maxMidi: 60 },  // E2–C4 (원서 미제시, 일반 합창 관행 기반 잠정치)
  },
}
export const VOICE_RANGES = VOICE_RANGES_BY_THEORY.baek // 하위호환용 기본값

export function getVoiceRanges(theoryBase = 'baek') {
  return VOICE_RANGES_BY_THEORY[theoryBase] ?? VOICE_RANGES_BY_THEORY.baek
}

export function getBeatsPerMeasure(ts) {
  return parseInt(ts.split('/')[0], 10)
}

const ACCIDENTAL_SEMITONES = { '': 0, n: 0, '#': 1, '##': 2, b: -1, bb: -2 }

export function pitchToMidi(pitch) {
  const m = pitch.match(/^([A-G])(##|#|bb|b|n)?(\d)$/)
  if (!m) return null
  const map = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 }
  const midi = (parseInt(m[3]) + 1) * 12 + map[m[1]]
  return midi + (ACCIDENTAL_SEMITONES[m[2] ?? ''] ?? 0)
}

const MIDI_SHARP_PC = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
const MIDI_FLAT_PC = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B']
const FLAT_KEY_SIGNATURES = new Set(['F', 'Bb', 'Eb', 'Ab', 'Db'])

/**
 * MIDI note number → kordAI pitch 문자열 (예: 60 → "C4").
 * @param {number} midi
 * @param {{ keySignature?: string }} [opts]
 */
export function midiToPitch(midi, { keySignature = 'C' } = {}) {
  if (!Number.isFinite(midi)) return null
  const m = Math.round(midi)
  if (m < 0 || m > 127) return null
  const pc = ((m % 12) + 12) % 12
  const oct = Math.floor(m / 12) - 1
  const preferFlats = FLAT_KEY_SIGNATURES.has(keySignature)
  const name = (preferFlats ? MIDI_FLAT_PC : MIDI_SHARP_PC)[pc]
  return `${name}${oct}`
}

export function isInRange(pitch, voice, theoryBase = 'baek') {
  const midi = pitchToMidi(pitch)
  if (midi === null) return true
  const { minMidi, maxMidi } = getVoiceRanges(theoryBase)[voice]
  return midi >= minMidi && midi <= maxMidi
}

// Groups a flat note array into measures
// Returns: Array of { notes, complete, beatsUsed }
export function groupIntoMeasures(notes, beatsPerMeasure) {
  const measures = []
  let current = []
  let beats = 0

  for (const note of notes) {
    const nb = BEAT_VALUES[note.duration] ?? 1

    // Adding this note would exceed the bar — close the current bar first
    if (beats + nb > beatsPerMeasure + 0.0001) {
      if (current.length > 0) {
        const full = Math.abs(beats - beatsPerMeasure) < 0.0001
        measures.push({
          notes: [...current],
          complete: full,
          beatsUsed: beats,
        })
      }
      current = [note]
      beats = nb
      if (Math.abs(beats - beatsPerMeasure) < 0.0001) {
        measures.push({ notes: [...current], complete: true, beatsUsed: beats })
        current = []
        beats = 0
      }
    } else {
      current.push(note)
      beats += nb
      if (Math.abs(beats - beatsPerMeasure) < 0.0001) {
        measures.push({ notes: [...current], complete: true, beatsUsed: beats })
        current = []
        beats = 0
      }
    }
  }

  if (current.length > 0) {
    measures.push({
      notes: [...current],
      complete: Math.abs(beats - beatsPerMeasure) < 0.0001,
      beatsUsed: beats,
    })
  }

  return measures
}

/** Measure index range [start,end] for flatIndex (current groupIntoMeasures grouping). */
export function getMeasureRangeForFlatIndex(notes, flatIndex, beatsPerMeasure) {
  if (flatIndex < 0 || flatIndex >= notes.length) return null
  const measures = groupIntoMeasures(notes, beatsPerMeasure)
  let offset = 0
  for (const m of measures) {
    if (flatIndex >= offset && flatIndex < offset + m.notes.length) {
      return { start: offset, end: offset + m.notes.length - 1 }
    }
    offset += m.notes.length
  }
  return null
}

/** True if new duration at flatIndex keeps that measure within beatsPerMeasure. */
export function canReplaceDurationInMeasure(notes, flatIndex, newDuration, beatsPerMeasure) {
  const r = getMeasureRangeForFlatIndex(notes, flatIndex, beatsPerMeasure)
  if (!r) return false
  let total = 0
  for (let i = r.start; i <= r.end; i++) {
    const d = i === flatIndex ? newDuration : notes[i].duration
    total += BEAT_VALUES[d] ?? 1
  }
  return total <= beatsPerMeasure + 0.0001
}

export function findPreviousPitch(notes, flatIndex) {
  for (let i = flatIndex - 1; i >= 0; i--) {
    const n = notes[i]
    if (!n.isRest && n.pitch) return n.pitch
  }
  return null
}

export function defaultPitchForVoice(voice) {
  const map = { soprano: 'C4', alto: 'G3', tenor: 'C3', bass: 'E2' }
  return map[voice] ?? 'C4'
}

// Count complete measures for a voice
export function countCompleteMeasures(notes, beatsPerMeasure) {
  return groupIntoMeasures(notes, beatsPerMeasure).filter(m => m.complete).length
}

// Get current measure info (measure number, beats used, beats per measure)
export function getCurrentMeasureInfo(notes, beatsPerMeasure) {
  const measures = groupIntoMeasures(notes, beatsPerMeasure)
  const completeMeasures = measures.filter(m => m.complete).length
  const currentMeasure = measures.find(m => !m.complete)
  
  if (currentMeasure) {
    return {
      measureNumber: completeMeasures + 1,
      beatsUsed: currentMeasure.beatsUsed,
      beatsPerMeasure,
      isComplete: false
    }
  } else {
    // No incomplete measure found - this means we're starting a new measure
    return {
      measureNumber: completeMeasures + 1,
      beatsUsed: 0,
      beatsPerMeasure,
      isComplete: false  // Changed from true to false
    }
  }
}

// Check if a note can be added to current measure without exceeding beats
export function canAddNoteToCurrentMeasure(notes, duration, beatsPerMeasure) {
  const info = getCurrentMeasureInfo(notes, beatsPerMeasure)
  const noteBeats = BEAT_VALUES[duration] ?? 1
  return info.beatsUsed + noteBeats <= beatsPerMeasure + 0.1
}

/** 성부에 이미 입력된 음(쉼표 포함) 총 박수 */
export function totalBeats(notes) {
  return (notes ?? []).reduce((sum, n) => sum + (BEAT_VALUES[n.duration] ?? 1), 0)
}

/**
 * targetBeats만큼을 쉼표로 채우기 위한 항목들(큰 음가부터 그리디하게 채움).
 * 빈 마디를 클릭해 그 위치에 음을 입력할 때, 그 전까지의 미기입 구간을
 * 쉼표로 메꿔서 실제로 그 마디에 음이 들어가도록 하는 용도.
 */
export function restEntriesForBeats(targetBeats) {
  const out = []
  let remaining = targetBeats
  while (remaining > 0.001) {
    if (remaining >= 3.999) { out.push({ duration: 'wr', isRest: true }); remaining -= 4 }
    else if (remaining >= 1.999) { out.push({ duration: 'hr', isRest: true }); remaining -= 2 }
    else if (remaining >= 0.999) { out.push({ duration: 'qr', isRest: true }); remaining -= 1 }
    else if (remaining >= 0.499) { out.push({ duration: '8r', isRest: true }); remaining -= 0.5 }
    else { out.push({ duration: 'qr', isRest: true }); remaining -= 1 }
  }
  return out
}

/** 새 악보: 각 성부에 한 마디분 쉼표(4/4면 온쉼표 1개 × 4성부)를 넣는다. */
export function blankScore(beatsPerMeasure) {
  const bar = restEntriesForBeats(beatsPerMeasure)
  const clone = () => bar.map((e) => ({ ...e }))
  return {
    soprano: clone(),
    alto: clone(),
    tenor: clone(),
    bass: clone(),
  }
}

/** 초기 템플릿(성부별 한 마디 쉼표만)인지 — "새로 만들기" 확인창용 */
export function isBlankTemplateScore(score, beatsPerMeasure) {
  const expected = restEntriesForBeats(beatsPerMeasure)
  return VOICE_NAMES.every((v) => {
    const notes = score?.[v] ?? []
    if (notes.length !== expected.length) return false
    return notes.every((n, i) => n?.isRest && n.duration === expected[i].duration)
  })
}

// Convert pitch "G4" to VexFlow key "g/4"
export function pitchToVex(pitch) {
  const m = pitch.match(/^([A-G])(##|#|bb|b|n)?(\d)$/)
  if (!m) return 'b/4'
  const acc = m[2] === 'n' ? '' : (m[2] ?? '')  // n은 VexFlow key에서 제거 (Accidental로 별도 추가)
  return `${m[1].toLowerCase()}${acc}/${m[3]}`
}

/** pitch accidental rotation: none -> # -> ## -> b -> bb -> n -> none */
export function rotateAccidentalInPitch(pitch) {
  const m = String(pitch).match(/^([A-G])(##|#|bb|b|n)?(\d+)$/)
  if (!m) return pitch
  const letter = m[1]
  const acc = m[2] ?? ''
  const oct = m[3]
  const order = ['', '#', '##', 'b', 'bb', 'n']
  const idx = order.indexOf(acc)
  const nextAcc = order[(idx + 1) % order.length]
  return nextAcc ? `${letter}${nextAcc}${oct}` : `${letter}${oct}`
}

/** pitch에 지정한 임시표를 직접 적용 (accidental: '', '#', '##', 'b', 'bb', 'n') */
export function setAccidentalInPitch(pitch, accidental) {
  const m = String(pitch).match(/^([A-G])(##|#|bb|b|n)?(-?\d+)$/)
  if (!m) return pitch
  const letter = m[1]
  const oct = m[3]
  return accidental ? `${letter}${accidental}${oct}` : `${letter}${oct}`
}

// VexFlow duration string for Tone.js duration
export function durationToVex(dur) {
  return dur  // 'w', 'h', 'q', '8' all valid in VexFlow
}

export function durationToTone(dur) {
  const map = { w: '1n', h: '2n', q: '4n', '8': '8n' }
  return map[dur] ?? '4n'
}

/** 조표별 온음계 음이름(자리표) — 악보 렌더링(ScoreRenderer)과 시벨리우스식 문자키 입력이 공유. */
export const KEY_SCALE_NOTES = {
  C:    ['C','D','E','F','G','A','B'],
  G:    ['G','A','B','C','D','E','F#'],
  D:    ['D','E','F#','G','A','B','C#'],
  A:    ['A','B','C#','D','E','F#','G#'],
  E:    ['E','F#','G#','A','B','C#','D#'],
  B:    ['B','C#','D#','E','F#','G#','A#'],
  'F#': ['F#','G#','A#','B','C#','D#','F'],
  F:    ['F','G','A','Bb','C','D','E'],
  Bb:   ['Bb','C','D','Eb','F','G','A'],
  Eb:   ['Eb','F','G','Ab','Bb','C','D'],
  Ab:   ['Ab','Bb','C','Db','Eb','F','G'],
  Db:   ['Db','Eb','F','Gb','Ab','Bb','C'],
}

/**
 * 시벨리우스식 문자키(A~G) 음표 입력: 조표에 맞는 자리표로 스펠링한 그 음이름을,
 * 기준 음(refPitch, 보통 직전 음)에서 가장 가까운 옥타브에 놓는다.
 */
export function nearestLetterPitch(letter, refPitch, keySignature = 'C') {
  const scale = KEY_SCALE_NOTES[keySignature] ?? KEY_SCALE_NOTES.C
  const spelled = scale.find((n) => n[0] === letter) ?? letter
  const refMidi = pitchToMidi(refPitch) ?? 60
  const refOctave = parseInt(String(refPitch).match(/(-?\d+)$/)?.[1] ?? '4', 10)
  let best = `${spelled}${refOctave}`
  let bestDist = Infinity
  for (let oct = refOctave - 1; oct <= refOctave + 1; oct++) {
    const candidate = `${spelled}${oct}`
    const midi = pitchToMidi(candidate)
    if (midi == null) continue
    const dist = Math.abs(midi - refMidi)
    if (dist < bestDist) {
      bestDist = dist
      best = candidate
    }
  }
  return best
}
